import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SpecraButton, SpecraInput, SpecraSelect } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { projectDataClient } from '../../../../lib/connect/clients';
export function SectionAssignments({ projectId, section, members, canEdit }) {
    const cache = useQueryClient();
    const [values, setValues] = useState({
        writerUserId: section.writerUserId ?? '',
        reviewerUserId: section.reviewerUserId ?? '',
        internalDate: section.internalDate ?? ''
    });
    const [edits, setEdits] = useState({});
    const [status, setStatus] = useState('');
    const saving = useRef(false);
    const dirty = useRef(false);
    useEffect(()=>{
        if (!dirty.current && !saving.current) setValues({
            writerUserId: section.writerUserId ?? '',
            reviewerUserId: section.reviewerUserId ?? '',
            internalDate: section.internalDate ?? ''
        });
    }, [
        section.writerUserId,
        section.reviewerUserId,
        section.internalDate
    ]);
    const update = useMutation({
        mutationFn: (patch)=>projectDataClient.updateOutlineSectionAssignments({
                projectId,
                sectionId: section.sectionId,
                ...patch
            }),
        onSuccess: async ()=>{
            await Promise.all([
                cache.invalidateQueries({
                    queryKey: [
                        'outline-sections',
                        projectId
                    ]
                }),
                cache.invalidateQueries({
                    queryKey: [
                        'proposal-assigned-actions'
                    ]
                })
            ]);
        }
    });
    const change = (field, value)=>{
        dirty.current = true;
        setValues((previous)=>({
                ...previous,
                [field]: value
            }));
        setEdits((previous)=>({
                ...previous,
                [field]: value
            }));
        setStatus('');
    };
    return /*#__PURE__*/ _jsxs("form", {
        "data-testid": Testids.proposalAssignments.editor,
        "data-section-id": section.sectionId,
        className: "space-y-2 rounded border border-border p-3",
        onSubmit: async (event)=>{
            event.preventDefault();
            if (saving.current || !canEdit || Object.keys(edits).length === 0) return;
            saving.current = true;
            setStatus('');
            try {
                const result = await update.mutateAsync(edits);
                if (result.section) setValues({
                    writerUserId: result.section.writerUserId ?? '',
                    reviewerUserId: result.section.reviewerUserId ?? '',
                    internalDate: result.section.internalDate ?? ''
                });
                dirty.current = false;
                setEdits({});
                setStatus('Assignments saved');
            } catch  {
                setStatus('Unable to save assignments. Your edits are retained. Retry assignments.');
            } finally{
                saving.current = false;
            }
        },
        children: [
            /*#__PURE__*/ _jsxs("fieldset", {
                disabled: !canEdit || update.isPending,
                className: "flex flex-wrap items-end gap-3",
                children: [
                    [
                        'writerUserId',
                        'reviewerUserId'
                    ].map((field)=>{
                        const role = field === 'writerUserId' ? 'writer' : 'reviewer';
                        return /*#__PURE__*/ _jsxs("div", {
                            children: [
                                /*#__PURE__*/ _jsxs("div", {
                                    className: "block",
                                    children: [
                                        "Section ",
                                        role,
                                        /*#__PURE__*/ _jsxs(SpecraSelect, {
                                            "aria-label": `Section ${role}`,
                                            "data-testid": field === 'writerUserId' ? Testids.proposalAssignments.writer : Testids.proposalAssignments.reviewer,
                                            value: values[field],
                                            onChange: (event)=>change(field, event.target.value),
                                            children: [
                                                /*#__PURE__*/ _jsx("option", {
                                                    value: "",
                                                    children: "Unassigned"
                                                }),
                                                values[field] && !members.some((member)=>member.userId === values[field]) && /*#__PURE__*/ _jsxs("option", {
                                                    value: values[field],
                                                    children: [
                                                        "Unavailable member (",
                                                        values[field],
                                                        ")"
                                                    ]
                                                }),
                                                members.map((member)=>/*#__PURE__*/ _jsx("option", {
                                                        value: member.userId,
                                                        children: member.name || member.email
                                                    }, member.userId))
                                            ]
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsxs(SpecraButton, {
                                    variant: "ghost",
                                    type: "button",
                                    "data-testid": field === 'writerUserId' ? Testids.proposalAssignments.clearWriter : Testids.proposalAssignments.clearReviewer,
                                    onClick: ()=>change(field, ''),
                                    children: [
                                        "Clear ",
                                        role
                                    ]
                                })
                            ]
                        }, field);
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        children: [
                            /*#__PURE__*/ _jsxs("div", {
                                className: "block",
                                children: [
                                    "Internal date",
                                    /*#__PURE__*/ _jsx(SpecraInput, {
                                        "aria-label": "Internal date",
                                        "data-testid": Testids.proposalAssignments.internalDate,
                                        type: "date",
                                        value: values.internalDate,
                                        onChange: (event)=>change('internalDate', event.target.value)
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsx(SpecraButton, {
                                variant: "ghost",
                                type: "button",
                                "data-testid": Testids.proposalAssignments.clearDate,
                                onClick: ()=>change('internalDate', ''),
                                children: "Clear internal date"
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        type: "submit",
                        "data-testid": Testids.proposalAssignments.save,
                        disabled: Object.keys(edits).length === 0,
                        isLoading: update.isPending,
                        children: update.isError ? 'Retry assignments' : 'Save assignments'
                    })
                ]
            }),
            !canEdit && /*#__PURE__*/ _jsx("p", {
                children: "Section assignments are read-only."
            }),
            update.isPending && /*#__PURE__*/ _jsx("output", {
                children: "Saving assignments…"
            }),
            status && (update.isError ? /*#__PURE__*/ _jsx("p", {
                role: "alert",
                children: status
            }) : /*#__PURE__*/ _jsx("output", {
                children: status
            }))
        ]
    });
}
